/* Clothxtra cascading mega menu */
if (!customElements.get('cx-cascade-menu')) {
  class CxCascadeMenu extends HTMLElement {
    connectedCallback() {
      this.openOn = this.dataset.openOn || 'hover';
      this.headerWrapper = this.closest('.header-wrapper');
      this.section = this.closest('.section-header');
      this.activeTrigger = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.lastPointer = 'mouse';

      this.triggers = Array.from(this.querySelectorAll('[data-cx-trigger]'));

      this.onKeyUp = this.onKeyUp.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDocClick = this.onDocClick.bind(this);

      this.addEventListener('pointerdown', (e) => (this.lastPointer = e.pointerType), { capture: true });

      this.triggers.forEach((trigger) => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        trigger._cxPanel = panel;

        trigger.addEventListener('mouseenter', () => {
          if (this.openOn !== 'hover') return;
          this.cancelClose();
          clearTimeout(this.openTimer);
          // open instantly when switching between top items, otherwise use a short intent delay
          const delay = this.activeTrigger ? 0 : 120;
          this.openTimer = setTimeout(() => this.open(trigger), delay);
        });
        trigger.addEventListener('mouseleave', () => clearTimeout(this.openTimer));

        trigger.addEventListener('click', (event) => {
          // first click/tap opens the panel; a second click follows the link
          const isOpen = this.activeTrigger === trigger;
          if (isOpen) return; // already open -> follow the link
          event.preventDefault();
          this.open(trigger);
        });

        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.open(trigger);
            panel.querySelector('.cx-list__link, .cx-col__heading a')?.focus();
          }
        });

        this.bindPanel(panel);
      });

      // Pointer leaving the nav + columns closes (hover mode)
      this.querySelectorAll('.cx-nav__item').forEach((item) => {
        item.addEventListener('mouseleave', (event) => {
          if (this.openOn !== 'hover' || !this.activeTrigger) return;
          const to = event.relatedTarget;
          if (to && (to.closest('.cx-panel__cols') || to.closest('[data-cx-trigger]'))) return;
          this.scheduleClose();
        });
      });
    }

    disconnectedCallback() {
      this.removeGlobalListeners();
    }

    bindPanel(panel) {
      const cols = panel.querySelector('.cx-panel__cols');
      cols.addEventListener('mouseenter', () => this.cancelClose());

      panel.querySelector('[data-cx-close]').addEventListener('click', () => this.close(true));
      panel.querySelector('[data-cx-overlay]').addEventListener('click', (event) => {
        if (event.target.closest('[data-cx-close]')) return;
        this.close(false);
      });
      panel.querySelector('[data-cx-overlay]').addEventListener('mouseenter', () => {
        if (this.openOn === 'hover' && this.lastPointer === 'mouse') this.scheduleClose(350);
      });

      panel.querySelectorAll('.cx-list__link').forEach((link) => {
        const activate = () => this.activateItem(panel, link);
        link.addEventListener('mouseenter', () => {
          if (this.openOn === 'hover' && this.lastPointer === 'mouse') activate();
        });
        link.addEventListener('focus', () => {
          // keyboard focus only; taps are handled by the click listener below
          if (link.matches(':focus-visible')) activate();
        });
        link.addEventListener('click', (event) => {
          if (!link.dataset.cxTarget) return;
          const expanded = link.getAttribute('aria-expanded') === 'true';
          // touch / click mode: first tap reveals the next column, second tap navigates
          if (!expanded) {
            event.preventDefault();
            activate();
          }
        });
        link.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight' && link.dataset.cxTarget) {
            event.preventDefault();
            activate();
            document.getElementById(link.dataset.cxTarget)?.querySelector('a')?.focus();
          }
          if (event.key === 'ArrowLeft') {
            const col = link.closest('[data-cx-col]');
            const level = Number(col?.dataset.cxCol || 1);
            if (level > 1) {
              event.preventDefault();
              panel.querySelector(`[data-cx-col="${level - 1}"] .cx-list__link[aria-expanded="true"]`)?.focus();
            }
          }
        });
      });
    }

    activateItem(panel, link) {
      const level = Number(link.dataset.cxLevel);
      const colIndex = level; // level 2 items open column 2, level 3 items open column 3
      // clear siblings in this column
      const ownCol = link.closest('[data-cx-col]');
      ownCol.querySelectorAll('.cx-list__link[aria-expanded]').forEach((l) => l.setAttribute('aria-expanded', 'false'));
      ownCol.querySelectorAll('.cx-list__link--active').forEach((l) => l.classList.remove('cx-list__link--active'));

      // hide deeper columns
      for (let c = colIndex; c <= 3; c++) {
        const col = panel.querySelector(`[data-cx-col="${c}"]`);
        if (!col) continue;
        col.hidden = true;
        col.querySelectorAll('.cx-sub').forEach((s) => (s.hidden = true));
        col.querySelectorAll('.cx-list__link[aria-expanded]').forEach((l) => l.setAttribute('aria-expanded', 'false'));
        col.querySelectorAll('.cx-list__link--active').forEach((l) => l.classList.remove('cx-list__link--active'));
      }

      if (!link.dataset.cxTarget) return;
      const target = document.getElementById(link.dataset.cxTarget);
      if (!target) return;
      const targetCol = target.closest('[data-cx-col]');
      target.hidden = false;
      targetCol.hidden = false;
      targetCol.scrollTop = 0;
      link.setAttribute('aria-expanded', 'true');
      link.classList.add('cx-list__link--active');
    }

    resetColumns(panel) {
      panel.querySelectorAll('[data-cx-col="2"], [data-cx-col="3"]').forEach((col) => (col.hidden = true));
      panel.querySelectorAll('.cx-sub').forEach((s) => (s.hidden = true));
      panel.querySelectorAll('.cx-list__link[aria-expanded]').forEach((l) => l.setAttribute('aria-expanded', 'false'));
      panel.querySelectorAll('.cx-list__link--active').forEach((l) => l.classList.remove('cx-list__link--active'));
    }

    setPanelHeight() {
      const bottom = this.headerWrapper.getBoundingClientRect().bottom;
      this.headerWrapper.style.setProperty('--cx-panel-height', `${Math.max(window.innerHeight - bottom, 240)}px`);
    }

    open(trigger) {
      this.cancelClose();
      if (this.activeTrigger === trigger) return;
      if (this.activeTrigger) this.hidePanel(this.activeTrigger);

      this.setPanelHeight();
      const panel = trigger._cxPanel;
      this.resetColumns(panel);
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.classList.add('cx-nav__top--open');
      this.activeTrigger = trigger;
      this.headerWrapper.classList.add('cx-menu-open');
      this.section?.classList.add('cx-menu-open');

      this.addGlobalListeners();
    }

    hidePanel(trigger) {
      trigger._cxPanel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove('cx-nav__top--open');
    }

    close(returnFocus) {
      clearTimeout(this.openTimer);
      this.cancelClose();
      if (!this.activeTrigger) return;
      const trigger = this.activeTrigger;
      this.hidePanel(trigger);
      this.activeTrigger = null;
      this.headerWrapper.classList.remove('cx-menu-open');
      this.section?.classList.remove('cx-menu-open');
      this.removeGlobalListeners();
      if (returnFocus) trigger.focus();
    }

    scheduleClose(delay = 250) {
      this.cancelClose();
      this.closeTimer = setTimeout(() => this.close(false), delay);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
    }

    addGlobalListeners() {
      if (this.listening) return;
      this.listening = true;
      this.scrollStart = window.scrollY;
      document.addEventListener('keyup', this.onKeyUp);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    removeGlobalListeners() {
      if (!this.listening) return;
      this.listening = false;
      document.removeEventListener('keyup', this.onKeyUp);
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    onKeyUp(event) {
      if (event.key === 'Escape') this.close(true);
    }

    onDocClick(event) {
      if (!this.contains(event.target)) this.close(false);
    }

    onScroll() {
      if (Math.abs(window.scrollY - this.scrollStart) > 80) this.close(false);
    }

    onResize() {
      if (window.innerWidth < 990) this.close(false);
      else this.setPanelHeight();
    }
  }

  customElements.define('cx-cascade-menu', CxCascadeMenu);
}
